import { useRef } from "react";
import { Control, Controller, FieldValues } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { UtcScheduleTimeHint } from "./UtcScheduleTimeHint";

interface ArrayFieldWrapperProps<TFieldValues extends FieldValues> {
  name: string;
  label: string;
  addLabel: string;
  fieldType: "time" | "text" | "number";
  summaryTemplate?: string;
  tooltip?: string;
  control: Control<TFieldValues>;
  isDisabled?: boolean;
  defaultValue?: string[];
  showUtcTimeHint?: boolean;
}

const ArrayFieldItem = ({
  value,
  onChange,
  onRemove,
  canRemove,
  fieldType,
  isDisabled,
  showUtcTimeHint,
}: {
  value: string;
  onChange: (value: string) => void;
  onRemove: () => void;
  canRemove: boolean;
  fieldType: "time" | "text" | "number";
  isDisabled?: boolean;
  showUtcTimeHint?: boolean;
}) => {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Input
          type={fieldType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isDisabled}
          className="flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] [&::-webkit-calendar-picker-indicator]:ml-auto"
        />
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label={`Remove ${fieldType}`}
            disabled={isDisabled}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {fieldType === "time" && showUtcTimeHint && (
        <UtcScheduleTimeHint value={value} />
      )}
    </div>
  );
};

const formatSummary = (template: string, count: number): string => {
  const timeLabel = count === 1 ? "time" : "times";
  return template
    .replace("{count}", String(count))
    .replace("{time_label}", timeLabel);
};

export const ArrayFieldWrapper = <TFieldValues extends FieldValues>({
  name,
  label,
  addLabel,
  fieldType,
  summaryTemplate,
  tooltip,
  control,
  isDisabled,
  defaultValue,
  showUtcTimeHint,
}: ArrayFieldWrapperProps<TFieldValues>) => {
  // Stable identity per row so React can match items across re-renders even
  // when their values are equal (e.g. two empty time slots).
  const idsRef = useRef<string[]>([]);
  const nextIdRef = useRef(0);
  const ensureIds = (count: number) => {
    while (idsRef.current.length < count) {
      idsRef.current.push(`row-${nextIdRef.current++}`);
    }
    if (idsRef.current.length > count) {
      idsRef.current = idsRef.current.slice(0, count);
    }
  };

  return (
    <Controller
      // RHF's `Path<TFieldValues>` is a string template type; we accept the
      // looser `string` at the prop layer because callers pass dynamic field
      // paths that aren't statically expressible.
      control={control}
      name={name as never}
      defaultValue={(defaultValue ?? [""]) as never}
      rules={{
        validate: (value: unknown) => {
          if (!Array.isArray(value) || value.length === 0) return false;
          return value.every(
            (v) => typeof v === "string" && v.trim().length > 0
          );
        },
      }}
      render={({ field }) => {
        const items: string[] = Array.isArray(field.value) ? field.value : [""];
        ensureIds(items.length);

        const handleAdd = () => {
          idsRef.current.push(`row-${nextIdRef.current++}`);
          field.onChange([...items, ""]);
        };

        const handleRemove = (index: number) => {
          idsRef.current.splice(index, 1);
          const next = items.filter((_, i) => i !== index);
          field.onChange(next.length > 0 ? next : [""]);
        };

        const handleChange = (index: number, value: string) => {
          const next = [...items];
          next[index] = value;
          field.onChange(next);
        };

        return (
          <div className="space-y-3">
            <div className="flex items-end justify-between">
              <NoxusFormLabel label={label} tooltip={tooltip} noMargin />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAdd}
                disabled={isDisabled}
                className="gap-1.5 text-sm"
              >
                <Plus className="h-3.5 w-3.5" />
                Add {addLabel}
              </Button>
            </div>

            <div className="space-y-2">
              {items.map((item, index) => (
                <ArrayFieldItem
                  key={idsRef.current[index]}
                  value={item}
                  onChange={(val) => handleChange(index, val)}
                  onRemove={() => handleRemove(index)}
                  canRemove={items.length > 1}
                  fieldType={fieldType}
                  isDisabled={isDisabled}
                  showUtcTimeHint={showUtcTimeHint}
                />
              ))}
            </div>

            {summaryTemplate && (
              <p className="text-sm text-muted-foreground">
                {formatSummary(summaryTemplate, items.length)}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};
